import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon } from './icon'

/**
 * Stamp — a perforated postage stamp for achievements and badges.
 *
 * Built for reuse by the Arcade hub (phase 3): pass `tint` with a game's own palette colour
 * and the stamp keeps the shared object language. Earned and locked states differ by more than
 * colour: earned stamps get a solid frame, a check seal and the "Earned" caption; locked stamps
 * are dashed, faded and say "Locked" (or your `caption`).
 */
export interface StampProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  label: string
  icon?: React.ReactNode
  earned?: boolean
  /** Visible caption under the label. Defaults to "Earned" / "Locked". */
  caption?: React.ReactNode
  /** Any CSS colour; defaults to the theme primary. */
  tint?: string
  /** Tilt in degrees; StampGrid assigns a gentle varied tilt when omitted. */
  tilt?: number
  /** Play the stamp-down animation on mount (e.g. just earned). Skipped under reduced motion. */
  fresh?: boolean
  as?: 'div' | 'li'
}

export const Stamp = React.forwardRef<HTMLElement, StampProps>(function Stamp(
  { label, icon, earned = false, caption, tint, tilt, fresh = false, as: Tag = 'div', className, style, ...props },
  ref,
) {
  const vars: React.CSSProperties = {
    ...(tint ? ({ '--stamp-tint': tint } as React.CSSProperties) : {}),
    ...(tilt !== undefined ? ({ '--stamp-tilt': `${tilt}deg` } as React.CSSProperties) : {}),
    ...style,
  }
  const status = earned ? 'Earned' : 'Locked'
  return (
    <Tag
      ref={ref as React.Ref<HTMLDivElement & HTMLLIElement>}
      data-earned={earned || undefined}
      data-fresh={(earned && fresh) || undefined}
      aria-label={`${label}: ${status}`}
      className={cn('v2-stamp', className)}
      style={vars}
      {...props}
    >
      <span className="v2-stamp-in" aria-hidden="true">
        {icon && <span className="v2-stamp-icon">{icon}</span>}
        <span className="v2-stamp-label">{label}</span>
        <span className="v2-stamp-caption">{caption ?? status}</span>
      </span>
      {earned && (
        <span className="v2-stamp-seal" aria-hidden="true">
          <Icon name="check" family="phosphor" size={14} weight="bold" />
        </span>
      )}
    </Tag>
  )
})

const TILTS = [-2.5, 1.5, -1, 2, -1.8, 0.8, 2.4, -0.6]

export interface StampGridProps extends React.HTMLAttributes<HTMLUListElement> {
  /** Colour of the surface behind the grid, so the perforation holes match. */
  surface?: 'table' | 'paper'
  children: React.ReactNode
}

/** Responsive grid of stamps. Children should be <Stamp as="li" />. */
export function StampGrid({ surface = 'table', className, children, ...props }: StampGridProps) {
  let i = 0
  const items = React.Children.map(children, (child) => {
    if (!React.isValidElement<StampProps>(child)) return child
    const tilt = child.props.tilt ?? TILTS[i++ % TILTS.length]
    return React.cloneElement(child, { as: 'li', tilt })
  })
  return (
    <ul data-surface={surface} className={cn('v2-stamp-grid', className)} {...props}>
      {items}
    </ul>
  )
}
